import React, { useState } from "react";
import { LockOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import viewpaths from "global/constants/ViewPathConstants";
import history from "utils/history";
import { RequestParticipant, SignRequest } from "models/interfaces";
import moment from "moment";
import {
  copySignRequest,
  downloadSignedFileAsZip,
  isRequestEditable,
  resendSignRequestHandle,
  voidSignRequestHandle,
} from "../MySignRequestHelper";
import { setReduxSignRequest } from "redux/appSlice";
import { AppModal, bodyTextSx } from "global/components/AppModal";
import strings from "global/constants/StringConstants";
import { useAppDispatch } from "redux/hooks";
import ViewDocuments from "./ViewDocuments";
import { hasAccessTo } from "layouts/Config";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import {
  computeSignerProgress,
  DATE_FORMAT,
  formatRequestDate,
  formatRequestDateTime,
  getRequestStatusVisual,
} from "./ViewRequestStatusMeta";
import filtersStyles from "screens/MySignRequests/SignRequestsFilters.styles";
import { getRequestTags } from "screens/MySignRequests/SignRequestsFiltersHelper";
import viewSignRequestStyles from "./ViewSignRequest.styles";
import viewSignRequestTokens, {
  viewSignRequestCompactTokens,
} from "./ViewSignRequest.tokens";
import {
  ArrowLeftGlyph,
  BellGlyph,
  CalendarGlyph,
  ChevronRightGlyph,
  ClockGlyph,
  DocumentGlyph,
  DownloadGlyph,
  KebabGlyph,
  UserGlyph,
} from "./ViewRequestIcons";

const styles = viewSignRequestStyles;
const { header, primaryButton, secondaryButton } = viewSignRequestTokens;
const {
  bar,
  header: compactHeader,
  progress: compactProgress,
} = viewSignRequestCompactTokens;

interface ViewSignRequestHeaderSectionProps {
  fetchData: Function;
  signRequest: SignRequest | any;
  isSigned: boolean;
  isLoading: boolean;
  setLoading: Function;
  setIsMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  handleMenuClose: (event: React.MouseEvent<HTMLElement>) => void;
  anchorEl: any;
  setAnchorEl: Function;
  isMenuOpen: any;
  setSaveTemplatePopup: (value: boolean) => void;
}

const ViewSignRequestHeaderSection: React.FC<
  ViewSignRequestHeaderSectionProps
> = ({
  fetchData,
  signRequest,
  isSigned,
  isLoading,
  setLoading,
  setIsMenuOpen,
  handleMenuClose,
  anchorEl,
  setAnchorEl,
  isMenuOpen,
  setSaveTemplatePopup,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  // The reference draws three columns: 375/390 phones, an 834 tablet and the
  // desktop this screen already shipped. Below `md` the breadcrumb becomes a
  // sticky bar that also carries the kebab, and the phone re-stacks the header
  // card entirely — neither is reachable from `sx` alone.
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = isCompact && !isPhone;

  const manualReminderState = useFeatureState(FeatureKeys.REMINDER_MANUAL);
  const isManualReminderAvailable =
    manualReminderState.status === strings.FEATURE_STATUS_AVAILABLE;
  const manualReminderTooltip =
    manualReminderState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((manualReminderState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : manualReminderState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : "";

  const templateUseState = useFeatureState(FeatureKeys.TEMPLATE_USE);
  const isTemplateUseAvailable =
    templateUseState.status === strings.FEATURE_STATUS_AVAILABLE;
  const templateUseTooltip =
    templateUseState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((templateUseState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : templateUseState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : "";

  const inPersonState = useFeatureState(FeatureKeys.ENVELOPE_IN_PERSON);
  const isInPersonAvailable =
    inPersonState.status === strings.FEATURE_STATUS_AVAILABLE;

  const [signRequestId, setPdfSignRequestId] = useState<string>("");
  const [voidPopup, setVoidPopup] = useState<boolean>(false);
  const [resendPopup, setResendPopup] = useState<boolean>(false);
  const [viewDocPopup, setViewDocPopup] = useState<boolean>(false);
  const [loader, setLoader] = useState(false);
  const [copyOptionsPopup, setCopyOptionsPopup] = useState<boolean>(false);
  const [copyWithValue, setCopyWithValue] = useState<boolean>(false);

  const status = getRequestStatusVisual(signRequest);

  const handleCopyConfirm = () => {
    copySignRequest(true, signRequest, dispatch, undefined, copyWithValue);
    setCopyOptionsPopup(false);
    setCopyWithValue(false);
    handleMenuClose({} as React.MouseEvent<HTMLElement>);
  };

  const canSendReminder =
    (signRequest?.status === strings.pending ||
      signRequest?.status === strings.inReview) &&
    hasAccessTo(strings.resources.sign, strings.sendPermission);

  // "Sign in person" is offered while the envelope is in progress and carries at
  // least one in-person signer. Actual readiness (whose turn it is) is decided
  // by the backend when the session is started; a not-ready start returns
  // `sessionComplete` and we surface a hint instead of entering hand-off.
  const hasInPersonSigner = (signRequest?.signers ?? []).some(
    (signer: RequestParticipant) =>
      signer?.deliveryMode === strings.deliveryModes.inPerson && !signer?.cc,
  );
  // Split from `isInPersonAvailable` so a gated request still shows the
  // button, locked with a tooltip, instead of disappearing outright — same
  // treatment "Send Reminder" already gets below.
  const inPersonEligible =
    hasInPersonSigner &&
    (signRequest?.status === strings.pending ||
      signRequest?.status === strings.inReview) &&
    hasAccessTo(strings.resources.sign, strings.sendPermission);
  const canLaunchInPerson = inPersonEligible && isInPersonAvailable;
  const inPersonTooltip =
    inPersonState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((inPersonState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : inPersonState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : "";

  // Same launch page either this button or the emailed host link reaches
  // (`/in-person/host`) — a plain navigation, no API call and no dialog here.
  // The consent, capture, and start logic that used to live in this file all
  // moved onto that page, so the flow is identical for both entry points.
  const handleInPersonLaunch = () => {
    if (!signRequest?.id) return;
    history.push(
      `${viewpaths.inPersonHost}?id=${encodeURIComponent(String(signRequest.id))}`,
    );  };

  const getSkeletonContent = () => (
    <Stack spacing={2}>
      <Skeleton variant="text" width={220} height={22} />
      <Box sx={styles.headerCard}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Skeleton variant="rounded" width={52} height={52} />
          <Stack spacing={1} flex={1}>
            <Skeleton variant="text" width="55%" height={30} />
            <Skeleton variant="text" width="70%" height={20} />
          </Stack>
          <Skeleton variant="rounded" width={180} height={44} />
        </Stack>
        <Skeleton variant="rounded" width="100%" height={7} sx={{ mt: 3 }} />
      </Box>
    </Stack>
  );

  const hasBeforeSigningPermission =
    hasAccessTo(strings.resources.sign, strings.sendPermission) ||
    hasAccessTo(strings.resources.template, strings.addPermission) ||
    (signRequest?.status === strings.pending &&
      hasAccessTo(strings.resources.sign, strings.fetchPermission)) ||
    ((signRequest?.status === strings.pending ||
      signRequest?.status === strings.scheduled ||
      signRequest?.status === strings.inReview) &&
      hasAccessTo(strings.resources.sign, strings.updatePermission));

  const getBreadcrumb = () => (
    <Box sx={styles.breadcrumb}>
      <Button
        disableRipple
        sx={styles.breadcrumbLink}
        onClick={() => history.push(viewpaths.sentSignRequestsViewPath)}
      >
        <ArrowLeftGlyph
          sx={{
            width: viewSignRequestTokens.breadcrumb.backIconSize,
            height: viewSignRequestTokens.breadcrumb.backIconSize,
          }}
        />
        Sign Requests
      </Button>
      <ChevronRightGlyph sx={styles.breadcrumbSeparator} />
      <Tooltip title={signRequest?.emailSubject ?? ""} arrow describeChild>
        <Typography
          component="span"
          aria-current="page"
          sx={styles.breadcrumbCurrent}
        >
          {signRequest?.emailSubject}
        </Typography>
      </Tooltip>
    </Box>
  );

  /**
   * Compact stand-in for the breadcrumb: a full-bleed sticky strip carrying the
   * back arrow, the trail and — since the header card has no room for it below
   * `md` — the request kebab.
   */
  const getPageBar = () => (
    <Box sx={styles.pageBar}>
      <IconButton
        disableRipple
        aria-label="Back to sign requests"
        onClick={() => history.push(viewpaths.sentSignRequestsViewPath)}
        sx={styles.pageBarBack}
      >
        <ArrowLeftGlyph
          sx={{ width: bar.backIconSize, height: bar.backIconSize }}
        />
      </IconButton>
      <Typography component="span" sx={styles.pageBarTitle}>
        Sign Requests
      </Typography>
      {isTablet && (
        <>
          <ChevronRightGlyph sx={styles.pageBarSeparator} />
          <Tooltip title={signRequest?.emailSubject ?? ""} arrow describeChild>
            <Typography
              component="span"
              aria-current="page"
              sx={styles.pageBarCurrent}
            >
              {signRequest?.emailSubject}
            </Typography>
          </Tooltip>
        </>
      )}
      {hasBeforeSigningPermission && (
        <IconButton
          disableRipple
          onClick={setIsMenuOpen}
          aria-label="Request options"
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          sx={styles.pageBarKebab}
        >
          <KebabGlyph
            sx={{ width: bar.kebabIconSize, height: bar.kebabIconSize }}
          />
        </IconButton>
      )}
    </Box>
  );

  const getStatusChip = () => (
    <Box sx={styles.statusChip(status.key)}>
      <Box component="span" sx={styles.statusDot(status.key)} />
      <Box component="span" sx={styles.statusLabel(status.key)}>
        {status.label}
      </Box>
    </Box>
  );

  /** The request's tags, in the list's own chip, beside the status. */
  const getTagChips = () =>
    getRequestTags(signRequest).map((tag) => (
      <Box
        key={tag.id}
        component="span"
        title={tag.name}
        sx={filtersStyles.tagChip(tag.color, true)}
      >
        {tag.name}
      </Box>
    ));

  /**
   * The phone column merges "Sent" and "Expires" onto one line and drops the
   * clock time; wider columns keep them as separate glyph-led items.
   */
  const getMetaLines = () => {
    const created = isPhone
      ? formatRequestDate(signRequest.createdOn)
      : formatRequestDateTime(signRequest.createdOn);
    const expires = signRequest?.expirationDays
      ? moment(signRequest.createdOn || new Date())
          .add(signRequest.expirationDays, "days")
          .format(DATE_FORMAT)
      : "";

    if (isPhone) {
      const dates = [
        created && `Sent ${created}`,
        expires && `Expires ${expires}`,
      ]
        .filter(Boolean)
        .join(" · ");

      return (
        <Box sx={styles.headerMetaRow}>
          <Box sx={styles.headerMetaItem}>
            Requested by&nbsp;
            <Box component="span" sx={styles.headerMetaStrong}>
              {signRequest.requestedBy?.name || "Unknown"}
            </Box>
          </Box>
          {dates && <Box sx={styles.headerMetaItem}>{dates}</Box>}
        </Box>
      );
    }

    return (
      <Box sx={styles.headerMetaRow}>
        <Box sx={styles.headerMetaItem}>
          <UserGlyph sx={styles.headerMetaIcon} />
          Requested by
          <Box component="span" sx={styles.headerMetaStrong}>
            {signRequest.requestedBy?.name || "Unknown"}
          </Box>
        </Box>
        {created && (
          <Box sx={styles.headerMetaItem}>
            <CalendarGlyph sx={styles.headerMetaIcon} />
            Sent {created}
          </Box>
        )}
        {expires && (
          <Box sx={styles.headerMetaItem}>
            <ClockGlyph sx={styles.headerMetaIcon} />
            Expires {expires}
          </Box>
        )}
      </Box>
    );
  };

  const getMetaRow = () => (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={styles.headerTitleRow}>
        <Typography component="h1" sx={styles.headerTitle}>
          {signRequest.emailSubject}
        </Typography>
        {getStatusChip()}
        {getTagChips()}
      </Box>
      {getMetaLines()}
    </Box>
  );

  const getActions = () => {
    return (
      <Box sx={styles.headerActions}>
        {canSendReminder && (
          <Tooltip
            title={isManualReminderAvailable ? "" : manualReminderTooltip}
            arrow
            disableHoverListener={isManualReminderAvailable}
            describeChild
          >
            <Box component="span" sx={styles.headerActionSlot}>
              <Button
                disableRipple
                disabled={!isManualReminderAvailable}
                onClick={() => handleResendPopup(signRequest.id)}
                sx={styles.secondaryButton}
              >
                <BellGlyph sx={styles.headerButtonIcon} />
                {isTablet ? "Remind" : "Send Reminder"}
              </Button>
            </Box>
          </Tooltip>
        )}
        {inPersonEligible && (
          <Tooltip
            title={isInPersonAvailable ? "" : inPersonTooltip}
            arrow
            disableHoverListener={isInPersonAvailable}
            describeChild
          >
            <Box component="span" sx={styles.headerActionSlot}>
              <Button
                disableRipple
                disabled={loader || !isInPersonAvailable}
                onClick={handleInPersonLaunch}
                endIcon={
                  !isInPersonAvailable ? (
                    <LockOutlined sx={{ fontSize: 14 }} />
                  ) : undefined
                }
                sx={styles.primaryButton}
              >
                <UserGlyph sx={styles.headerButtonIcon} />
                {strings.inPerson.launchLabel}
              </Button>
            </Box>
          </Tooltip>
        )}
        {isSigned &&
          hasAccessTo(strings.resources.sign, strings.downloadPermission) && (
            <Button
              disableRipple
              disabled={loader}
              onClick={() =>
                downloadSignedFileAsZip(
                  signRequest,
                  setLoader,
                  signRequest.id,
                  signRequest.signRequestConfig,
                )
              }
              sx={styles.primaryButton}
            >
              <DownloadGlyph sx={styles.headerButtonIcon} />
              {loader
                ? "Downloading..."
                : isTablet
                  ? "Download"
                  : "Download Documents"}
            </Button>
          )}
        {/* Below `md` the kebab lives in the sticky bar instead. */}
        {!isCompact && hasBeforeSigningPermission && (
          <IconButton
            disableRipple
            onClick={setIsMenuOpen}
            aria-label="Request options"
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
            sx={styles.headerKebab}
          >
            <KebabGlyph
              sx={{
                width: secondaryButton.kebabIconSize,
                height: secondaryButton.kebabIconSize,
              }}
            />
          </IconButton>
        )}
      </Box>
    );
  };

  const getProgressStrip = () => {
    // Signing progress counts signers only — approvers are not signers, so they
    // must not inflate the "X of N" tally.
    const { signed, total, pct } = computeSignerProgress(signRequest.signers);
    if (total === 0 || signRequest.status === strings.scheduled) return null;

    return (
      <Box sx={styles.progressStrip}>
        <Box component="span" sx={styles.progressLabel}>
          {isPhone ? compactProgress.label[1] : compactProgress.label[2]}
        </Box>
        <Box sx={styles.progressTrack}>
          <Box sx={styles.progressFill(status.key, pct)} />
        </Box>
        <Box component="span" sx={styles.progressValue}>
          {signed} of {total} completed
        </Box>
      </Box>
    );
  };

  const getDocumentTile = () => (
    <Box sx={styles.headerIconTile(status.key)}>
      <DocumentGlyph sx={styles.headerIconGlyph} />
    </Box>
  );

  /**
   * The phone column of the reference unstacks the header: title beside the
   * tile, then the status chip, the meta lines, the progress strip and finally
   * two full-width actions. Only the ≥sm shape keeps the desktop three-column
   * row, so the two are separate trees rather than one with `sx` overrides.
   */
  const getHeaderCard = () => {
    if (!signRequest) return null;

    return (
      <>
        {isCompact ? getPageBar() : getBreadcrumb()}
        <Box sx={styles.headerCard}>
          {isPhone ? (
            <>
              <Box sx={styles.headerRow}>
                {getDocumentTile()}
                <Box sx={{ minWidth: 0 }}>
                  <Typography component="h1" sx={styles.headerTitle}>
                    {signRequest.emailSubject}
                  </Typography>
                </Box>
              </Box>
              <Box sx={styles.headerChipRow}>
                {getStatusChip()}
                {getTagChips()}
              </Box>
              {getMetaLines()}
              {getProgressStrip()}
              {getActions()}
            </>
          ) : (
            <>
              <Box sx={styles.headerRow}>
                {getDocumentTile()}
                {getMetaRow()}
                {getActions()}
              </Box>
              {getProgressStrip()}
            </>
          )}
        </Box>
      </>
    );
  };

  /*
    Same gate the list's kebab uses (`getRequestMenuItems`): the request is
    still pending or in review, nobody it is waiting on has acted, and the user
    holds the sign update permission. `isRequestEditable` is shared rather than
    restated so the two menus can never drift apart.
  */
  const canUpdateRequest =
    (signRequest?.status === strings.pending ||
      signRequest?.status === strings.inReview) &&
    isRequestEditable(signRequest) &&
    hasAccessTo(strings.resources.sign, strings.updatePermission);

  /**
   * The editor reads `unsignedDoc` / `unsignedDocUrl`, but a *sent* request
   * comes back with the PDF variants — normalise before handing it over, as the
   * list's menu does.
   */
  const handleUpdateRequest = (event: any) => {
    const normalized = {
      ...signRequest,
      files: (signRequest?.files || []).map((file: any) => ({
        ...file,
        unsignedDoc: file.unsignedDoc || file.unsignedPdf || "",
        unsignedDocUrl: file.unsignedDocUrl || file.unsignedPdfUrl || "",
      })),
    } as any;
    dispatch(setReduxSignRequest(normalized));
    handleMenuClose(event);
    history.push({
      pathname: `${viewpaths.updateSignRequestsViewPath}`,
      state: signRequest,
    });
  };

  /** Props shared by both kebab dropdowns so they stay visually identical. */
  const menuChrome = {
    anchorEl,
    open: isMenuOpen,
    onClose: handleMenuClose,
    anchorOrigin: { vertical: "bottom", horizontal: "right" } as const,
    transformOrigin: { vertical: "top", horizontal: "right" } as const,
    PaperProps: { sx: styles.menuPaper },
    MenuListProps: { sx: styles.menuList },
  };

  const beforeSigningMenu = (
    <Menu {...menuChrome}>
      {(signRequest?.status === strings.pending ||
        signRequest?.status === strings.inReview) &&
        hasAccessTo(strings.resources.sign, strings.sendPermission) && (
          <Tooltip
            title={isManualReminderAvailable ? "" : manualReminderTooltip}
            placement="left"
            arrow
            disableHoverListener={isManualReminderAvailable}
            describeChild
          >
            <MenuItem
              disableRipple
              disabled={!isManualReminderAvailable}
              onClick={(e: any) => {
                if (!isManualReminderAvailable) return;
                handleResendPopup(signRequest.id);
                handleMenuClose(e);
              }}
              sx={
                isManualReminderAvailable
                  ? styles.menuItem
                  : styles.menuItemLocked
              }
            >
              Resend
              {!isManualReminderAvailable && (
                <LockOutlined sx={styles.menuItemLock} />
              )}
            </MenuItem>
          </Tooltip>
        )}
      {canUpdateRequest && (
        <MenuItem
          disableRipple
          onClick={handleUpdateRequest}
          sx={styles.menuItem}
        >
          {strings.update}
        </MenuItem>
      )}
      {hasAccessTo(strings.resources.sign, strings.sendPermission) && (
        <MenuItem
          disableRipple
          onClick={(e) => {
            setCopyOptionsPopup(true);
            handleMenuClose(e);
          }}
          sx={styles.menuItem}
        >
          Copy/Duplicate
        </MenuItem>
      )}
      {hasAccessTo(strings.resources.template, strings.addPermission) && (
        <Tooltip
          title={templateUseTooltip}
          placement="left"
          arrow
          disableHoverListener={isTemplateUseAvailable}
          describeChild
        >
          <MenuItem
            disableRipple
            disabled={!isTemplateUseAvailable}
            onClick={(e) => {
              if (!isTemplateUseAvailable) return;
              setSaveTemplatePopup(true);
              handleMenuClose(e);
            }}
            sx={
              isTemplateUseAvailable ? styles.menuItem : styles.menuItemLocked
            }
          >
            Save as Template
            {!isTemplateUseAvailable && (
              <LockOutlined sx={styles.menuItemLock} />
            )}
          </MenuItem>
        </Tooltip>
      )}
      {signRequest?.status === strings.pending &&
        hasAccessTo(strings.resources.sign, strings.fetchPermission) && (
          <MenuItem
            disableRipple
            onClick={() => setViewDocPopup(true)}
            sx={styles.menuItem}
          >
            View Document
          </MenuItem>
        )}
      {(signRequest?.status === strings.pending ||
        signRequest?.status === strings.scheduled ||
        signRequest?.status === strings.inReview) &&
        hasAccessTo(strings.resources.sign, strings.updatePermission) && (
          <MenuItem
            disableRipple
            onClick={(e: any) => {
              handleVoidPopup(signRequest.id);
              handleMenuClose(e);
            }}
            sx={styles.menuItemDestructive}
          >
            Void
          </MenuItem>
        )}
    </Menu>
  );

  const afterSigningMenu = (
    <Menu {...menuChrome}>
      <MenuItem
        disableRipple
        onClick={(e) => {
          setCopyOptionsPopup(true);
          handleMenuClose(e);
        }}
        sx={styles.menuItem}
      >
        Copy/Duplicate
      </MenuItem>
      <Tooltip
        title={templateUseTooltip}
        placement="left"
        arrow
        disableHoverListener={isTemplateUseAvailable}
        describeChild
      >
        <MenuItem
          disableRipple
          disabled={!isTemplateUseAvailable}
          onClick={(e) => {
            if (!isTemplateUseAvailable) return;
            setSaveTemplatePopup(true);
            handleMenuClose(e);
          }}
          sx={isTemplateUseAvailable ? styles.menuItem : styles.menuItemLocked}
        >
          Save as Template
          {!isTemplateUseAvailable && (
            <LockOutlined sx={styles.menuItemLock} />
          )}
        </MenuItem>
      </Tooltip>
    </Menu>
  );

  const handleCloseResendPopup = () => {
    setPdfSignRequestId("");
    setResendPopup(false);
  };

  const handleCloseViewDocPopup = () => {
    setPdfSignRequestId("");
    setViewDocPopup(false);
  };

  const handleResendConfirm = async () => {
    await resendSignRequestHandle(
      signRequestId,
      setLoading,
      setResendPopup,
      setAnchorEl,
    );
    fetchData();
  };

  const handleResendPopup = (reqId: string) => {
    setPdfSignRequestId(reqId);
    setResendPopup(true);
  };

  const handleVoidPopup = (reqId: string) => {
    setPdfSignRequestId(reqId);
    setVoidPopup(true);
  };

  const handleVoidConfirm = async () => {
    await voidSignRequestHandle(
      signRequestId,
      setLoading,
      setVoidPopup,
      setAnchorEl,
    );
    fetchData();
  };

  const handleCloseVoidPopup = () => {
    setPdfSignRequestId("");
    setVoidPopup(false);
    setAnchorEl(null);
  };

  if (isLoading) {
    return <>{getSkeletonContent()}</>;
  }

  return (
    <>
      {getHeaderCard()}
      {isSigned ? afterSigningMenu : beforeSigningMenu}
      <AppModal
        open={voidPopup}
        onClose={handleCloseVoidPopup}
        title="Really want to void request !"
        // Voiding cancels every pending signature and cannot be undone.
        tone="destructive"
        size="sm"
        primaryLabel={strings.voidBtn}
        onPrimary={handleVoidConfirm}
        cancelLabel="Cancel"
        onCancel={handleCloseVoidPopup}
      >
        <Typography sx={bodyTextSx}>
          Once a request is voided, it cannot be completed and must be created a
          new.
        </Typography>
      </AppModal>
      <AppModal
        open={resendPopup}
        onClose={handleCloseResendPopup}
        title="Really want to resend request !"
        tone="neutral"
        size="sm"
        primaryLabel={strings.Resend}
        onPrimary={handleResendConfirm}
        cancelLabel="Cancel"
        onCancel={handleCloseResendPopup}
      >
        <Typography sx={bodyTextSx}>
          Once you resend request, current signer will get notified about this
          sign request.
        </Typography>
      </AppModal>
      <AppModal
        open={viewDocPopup}
        onClose={handleCloseViewDocPopup}
        title="Documents"
        tone="neutral"
        // A document viewer — the widest step the system offers.
        size="xl"
      >
        <ViewDocuments request={signRequest} />
      </AppModal>
      <AppModal
        open={copyOptionsPopup}
        onClose={() => {          setCopyOptionsPopup(false);
          setCopyWithValue(false);
        }}
        title="Copy/Duplicate request"
        size="sm"
        primaryLabel="Copy"
        onPrimary={handleCopyConfirm}
        cancelLabel="Cancel"
        onCancel={() => {
          setCopyOptionsPopup(false);
          setCopyWithValue(false);
        }}
      >
        <Stack spacing={0}>
          <Typography sx={bodyTextSx}>
            Choose whether to copy existing field values into the new request.
            Text and checkbox values will be kept only if you enable the option
            below.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={copyWithValue}
                onChange={(e) => setCopyWithValue(e.target.checked)}
                size="small"
              />
            }
            label="Keep values"
          />
        </Stack>
      </AppModal>
    </>
  );
};

export default ViewSignRequestHeaderSection;
